import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Modal, Typography, Space, Button, Spin } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { useCurrentUser } from 'store/users/selectors';
import * as Sentry from '@sentry/react';
import { apis } from 'utils/lib/apis';

const { Text } = Typography;

const ViewUserInfoModal = forwardRef(({ username: usernameProp, password: passwordProp }, ref) => {
    const currentUser = useCurrentUser();
    const [visible, setVisible] = useState(false);
    const [copied, setCopied] = useState({ username: false, password: false });
    const [fetchedPassword, setFetchedPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    useImperativeHandle(ref, () => ({
        open: () => {
            setVisible(true);
            setCopied({ username: false, password: false });
        },
        close: () => {
            setVisible(false);
        }
    }));

    useEffect(() => {
        if (!visible) {
            setFetchedPassword('');
            return;
        }
        if (passwordProp) {
            setFetchedPassword(String(passwordProp));
            return;
        }
        const uid = currentUser?.id;
        const uname = currentUser?.username;
        if (!uid && !uname) {
            setFetchedPassword('');
            return;
        }
        let cancelled = false;
        (async () => {
            setPasswordLoading(true);
            try {
                const response = await apis.post('usersRDS', '/get_user_password', {
                    body: { id: uid, username: uname },
                });
                if (!cancelled && response?.success && response?.data?.password) {
                    setFetchedPassword(String(response.data.password));
                } else if (!cancelled) {
                    setFetchedPassword('N/A');
                }
            } catch (error) {
                Sentry.captureException(error);
                if (!cancelled) setFetchedPassword('N/A');
            } finally {
                if (!cancelled) setPasswordLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [visible, passwordProp, currentUser?.id, currentUser?.username]);

    const handleCopy = async (text, type) => {
        if (!text || text === 'N/A') return;
        try {
            await navigator.clipboard.writeText(text);
            setCopied(prev => ({ ...prev, [type]: true }));
            setTimeout(() => {
                setCopied(prev => ({ ...prev, [type]: false }));
            }, 2000);
        } catch (err) {
            Sentry.captureException(err);
        }
    };

    const username = usernameProp || currentUser?.username || 'N/A';
    const password = fetchedPassword || 'N/A';

    return (
        <Modal
            className='userInfoCstm'
            title="User Information"
            open={visible}
            onCancel={() => setVisible(false)}
            width={500}
            footer={[
                <Button key="close" onClick={() => setVisible(false)}>
                    Close
                </Button>
            ]}
        >
            <Space direction="vertical" size="large" style={{ width: '100%', marginTop: 20 }}>
                <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        Username:
                    </Text>
                    <Space>
                        <Text code style={{ fontSize: 14, padding: '8px 12px', display: 'inline-block', flex: 1 }}>
                            {username}
                        </Text>
                        <Button
                            icon={copied.username ? <CheckOutlined /> : <CopyOutlined />}
                            onClick={() => handleCopy(username, 'username')}
                            size="small"
                            disabled={username === 'N/A'}
                        >
                            {copied.username ? 'Copied' : 'Copy'}
                        </Button>
                    </Space>
                </div>
                <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        Password:
                    </Text>
                    <Space align="start">
                        {passwordLoading ? (
                            <Spin />
                        ) : (
                            <Text code style={{ fontSize: 14, padding: '8px 12px', display: 'inline-block', flex: 1 }}>
                                {password || 'N/A'}
                            </Text>
                        )}
                        <Button
                            icon={copied.password ? <CheckOutlined /> : <CopyOutlined />}
                            onClick={() => handleCopy(password, 'password')}
                            size="small"
                            disabled={!password || password === 'N/A' || passwordLoading}
                        >
                            {copied.password ? 'Copied' : 'Copy'}
                        </Button>
                    </Space>
                </div>
            </Space>
        </Modal>
    );
});

ViewUserInfoModal.displayName = 'ViewUserInfoModal';

export default ViewUserInfoModal;
